import React, { useEffect, useState } from 'react';
import {
  TextField, Checkbox, Select, MenuItem, FormControlLabel, Autocomplete, Button, FormControl, InputLabel, Switch, IconButton, FormHelperText
} from '@mui/material';
import styled from 'styled-components';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { CalendarIcon, LocalizationProvider } from '@mui/x-date-pickers';
import moment, { MomentInput } from 'moment';
import { Replay, VerifiedUser } from '@mui/icons-material';
import { getDeepCopy, isEmpty } from '../../utils/helpers';
import { TRANSACTION_TYPES } from '../../utils/constant';
import useService from '../../hooks/useService';


const Form = styled.form`
  display:flex;
  gap:20px; 
  flex-direction:column; 
`

const Fieldset = styled.div`
  display:flex;
  gap:20px;  
  flex-wrap:wrap;
  justify-content:space-between;
`
const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  const key = event.key
  const isLetter = /^[#.0-9a-zA-Z \s,/-]+$/.test(key)
  const isSpace = key === " "
  const isControlKey = key === "Backspace" || key === "Delete" || key === "ArrowLeft" || key === "ArrowRight"
  if (!isLetter && !isSpace && !isControlKey) {
      event.preventDefault()
  }
}

export const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

// export type TFieldType = 'number' | 'text' | 'password' | 'switch' | 'checkbox' | 'comment'
export type TFieldType = 'number' | 'text' | 'password' | 'select' | 'switch' | 'checkbox' | 'autocomplete' | 'date' | 'comment' | 'confirmationField' | 'custom'
export type ConditionalOperators = 'eq' | 'ne'

export type TCondition<T> = {
  fieldName: keyof T;
  value: any;
  operator: ConditionalOperators
}

export class Condition<T> {
  fieldName: keyof T;
  value: any;
  operator: ConditionalOperators;

  constructor(condition: TCondition<T>) {
    this.fieldName = condition.fieldName
    this.value = condition.value
    this.operator = condition.operator
  }

  isConditionValid = (formData: T) => {
    let isValid = false
    switch (this.operator) {
      case 'eq':
        isValid = formData[this.fieldName] === this.value
        break;

      case 'ne':
        isValid = formData[this.fieldName] != this.value
        break;
    }
    return isValid
  }
}

export class ConditionalValue<T> {
  conditions: {
    value: any;
    condition: Condition<T>;
  }[]
  value: any;

  constructor(params: TConditionalValue<T>[]) {
    this.conditions = params.map((item: TConditionalValue<T>) => (
      { ...item, condition: new Condition(item.condition) }
    ))
  }

  isAnyConditionValid = (data: T) => {
    this.value = undefined
    let isValid = false
    for (let condition of this.conditions) {
      if (condition.condition.isConditionValid(data)) {
        this.value = condition.value
        isValid = true
        break;
      }
    }
    return isValid
  }
}

export type TConditionalValue<T> = {
  value: any;
  condition: TCondition<T>;
}

export type TFieldValidation<T> = {
  required?: boolean | Condition<T>;
  min?: number | ConditionalValue<T>;
  max?: number | ConditionalValue<T>;
  minLength?: number | ConditionalValue<T>;
  maxLength?: number | ConditionalValue<T>;
  contain?: string[] | number[] | string | ConditionalValue<T>;
  regex?: RegExp | ConditionalValue<T>;
  email?: boolean;
}

// export type TDateField = {
//   fieldType: 'date';
//   minDate?: any;
//   maxDate?: any;
//   disableFuture?: boolean;
// }

// export type TSelectableField = {
//     fieldType: 'select' | 'autocomplete',
//     options?: { label: string, value: any }[];
// }

// export type TConfirmationField = {
//     fieldType: 'confirmationField',
//     isVerified?: boolean;
// }

// export type TCommentField = {
//     fieldType: 'comment',
//     label: string;
// }

// export type TField<T> = {
//   fieldName: keyof T;
//   label: string;
//   fieldType: TFieldType;
//   isVisible?: boolean;
//   fullWidth?: boolean;
//   width?: string;
//   disabled?: boolean;
//   value?: any;
//   initialValue?: any;
//   validation?: TFieldValidation<T>;
//   onChange?: (value: any, data: T) => any;
//   onConfirm?: (value: any, data: T) => any
// }
export type TField<T> = {
  fieldName: keyof T;
  label: string;
  fieldType: TFieldType;
  isVisible?: boolean;
  fullWidth?: boolean;
  width?: string;
  disabled?: boolean;
  value?: any;
  initialValue?: any;
  validation?: TFieldValidation<T>;
  isVerified?: boolean;
  options?: { label: string, value: any }[];
  disableFuture?: boolean;
  numbersOnly?: boolean;
  regexFilter?: RegExp;
  decimalPlaces?: number;
  disabledManualInput?: boolean;
  freeSolo? : boolean;
  defaultValue? : string 
  onChange?: (value: any, data: T) => any;
  onConfirm?: (value: any, data: T) => any;
  RenderField?: (props: TDynamicFieldProps<T>) => React.JSX.Element
  component?: (props: TDynamicFieldProps<any>) => React.JSX.Element
}

export type TFormError = {
  fieldName: string,
  error: string
}

export type TDynamicFieldProps<T> = {
  config: TField<T>;
  formData: T;
  onChange: (value: any) => any
  error: string;
}

type TDynamicFormProps<T> = {
  formData: T;
  config: TField<T>[];
  errors: TFormError[];
  submitText?: string | null;
  cancelText?: string;
  onCancel?: () => any
  onSubmit?: (formData: T) => any
  changeHandler: (value: any, fieldConfig: TField<T>) => any;
  submitHandler: (handler: (formData: T) => any) => any;
  cancelHandler: (handler: () => any) => any;
}

type TUseDynamicFormProps<T> = {
  formConfig: TField<T>[];
}

// This is the hook to manage the state of the Form
export function useDynamicForm<T>({ formConfig }: TUseDynamicFormProps<T>) {
  const [formData, setFormData] = useState<T>(getInitialValue(formConfig));
  const [errors, setErrors] = useState<TFormError[]>([])
  const [config, setConfig] = useState<TField<T>[]>(formConfig)
  const { store } = useService();
  const { transactionsStore } = store;
  const { transactionInfo } = transactionsStore;

  const changeHandler = (value: any, fieldConfig: TField<T>) => {
    const fieldName = fieldConfig.fieldName as string;

    // Only for BUY_SELL
    if (
      transactionInfo.transactionType === TRANSACTION_TYPES.BUY_SELL ||
      fieldName === 'customerTaxInvoicePreTaxValue' ||
      fieldName === 'customerTaxInvoiceDeclaredValue'
    ) {

      const TAX_PERCENT = 3;
      const MULTIPLIER = 1 + TAX_PERCENT / 100;

      // When PreTax changes → update Declared
      if (fieldName === 'customerTaxInvoicePreTaxValue') {

        const preTax = Number(value);

        if (!isNaN(preTax) && preTax > 0) {

          const declared = +(preTax * MULTIPLIER).toFixed(2);

          setFormData((prevData: any) => ({
            ...prevData,
            customerTaxInvoicePreTaxValue: value,
            customerTaxInvoiceDeclaredValue: declared
          }));

          return
        }
      }

      // When Declared changes → update PreTax
      if (fieldName === 'customerTaxInvoiceDeclaredValue') {
        const declared = Number(value);

        if (!isNaN(declared) && declared > 0) {

          const preTax = +(declared / MULTIPLIER).toFixed(2);

          setFormData((prevData: any) => ({
            ...prevData,
            customerTaxInvoiceDeclaredValue: value,
            customerTaxInvoicePreTaxValue: preTax
          }));

          return
        }
      }
    }
    if (fieldConfig.onChange && fieldConfig.fieldType != 'autocomplete') {
      fieldConfig.onChange(value, formData);
    }
    if (fieldConfig.numbersOnly) value = value.replace(/[^0-9]/g, '')
    if (fieldConfig.regexFilter && !fieldConfig.regexFilter.test(value) && value) {
      value = formData[fieldConfig.fieldName] ?? ''
    }
    setFormData((prevData: any) => ({ ...prevData, [fieldConfig.fieldName]: value }));
  };

  const setField = (fieldName:keyof T, config:Partial<TField<T>>) => {
    setConfig(prev => prev.map(field =>{
      if(field.fieldName === fieldName) field = {...field, ...config }
      return field
    }))
  }

  const submitHandler = (handler: (formData: T) => void) => {
    setErrors([])
    const validation = validateForm(config, formData)
    if (validation.length > 0) {
      setErrors(validation)
      return
    }
    handler(formData)
  }

  const cancelHandler = (handler: () => any) => {
    setFormData({} as T)
    handler()
  }


  return {
    registerForm: () => ({
      formData,
      errors,
      changeHandler,
      submitHandler,
      cancelHandler,
      config
    }),
    setFormConfig: setConfig,
    formData,
    setFormData,
    setField
  }
}

// This the main Form Jsx
function DynamicForm<T>(props: TDynamicFormProps<T>) {
  const {
    /* These are the methods & properties defined by useDynamicForm hook 
    & will be passed down by spreading the invoked register function given by the hook 
    on DynamicForm component as props */
    submitHandler,
    cancelHandler,
    changeHandler,
    config,
    formData,
    errors,

    // These are the methods & properties client component will pass
    onSubmit,
    onCancel,
    submitText,
    cancelText,
  } = props

  const [isReady, setIsReady] = useState(false)


  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submitHandler(onSubmit!)
  }

  const handleCancel = () => {
    cancelHandler(onCancel!)
  }

  useEffect(() => {
    !isReady && setIsReady(true)
  }, [isReady])


  return (
    isReady ?
      <Form onSubmit={handleSubmit} noValidate>
        <Fieldset>
          {config.map(fieldConfig => {
            const { isVisible, fullWidth, width, ...fieldProps } = fieldConfig
            const fieldError = errors.filter(error => error.fieldName === fieldConfig.fieldName)[0]
            if (isVisible) {
              return (
                <div key={fieldConfig.fieldName as string} style={{ width: fullWidth ? 'calc(100% - 10px)' : (Boolean(width) ? width : 'calc(50% - 10px)') }}>
                  <DynamicField
                    config={fieldProps}
                    formData={formData}
                    onChange={(value: any): any => {
                      changeHandler(value, fieldConfig)
                    }}
                    error={fieldError ? fieldError.error : ''}
                  />
                </div>
              )
            }
          })}
        </Fieldset>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', gap: '20px' }}>
          {onCancel ? <Button type='button' variant='outlined' color='secondary' onClick={handleCancel}>{cancelText || 'Cancel'}</Button> : <></>}
          {onSubmit ? submitText === null ? <></> : <Button variant='contained' type='submit'>{submitText || 'Submit'}</Button> : <></>}
        </div>
      </Form> : <></>
  )
}

// This is the main field used in the form
function DynamicField<T>(props: TDynamicFieldProps<T>) {
  const { fieldType, label } = props.config;

  switch (fieldType) {
    case 'number':
    case 'text':
    case 'password':
      return <DynamicTextField {...props} />;

    case 'checkbox':
      return <DynamicCheckbox {...props} />;

    case 'select':
      return <DynamicSelectField {...props} />

    case 'autocomplete':
      return <DynamicAutocomplete {...props} />

    case 'date':
      return <DynamicDateField {...props} />

    case 'switch':
      return <DynamicSwitch {...props} />

    case 'comment':
      return <label>{label}</label>

    case 'confirmationField':
      return <DynamicConfirmationField {...props} />

    case 'custom':
      return <DynamicCustomField {...props} />

    default:
      return null;
  }
}

let burstStringHolder:any = "";
let burstInputInitiated:any = false;
let timeoutHolder:any = null;

// These are the different types of field used in the main field according to the config
function DynamicTextField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { fieldType, fieldName, label, disabled, validation, decimalPlaces, disabledManualInput,defaultValue } = config;
  const [isFocused, setIsFocused] = useState(false);
  function extractNumericPart(str:string) {
    // Use a regular expression to match the numeric part of the string
    const match = str.match(/[+-]?\d+(\.\d+)?/);

    // If a match is found, return it as a number
    if (match) {
        return parseFloat(match[0]).toString();
    }

    // If no numeric part is found, return NaN (Not-a-Number)
    return NaN;
}

  return (
    <TextField
      type={fieldType}
      onChange={(e) => {
        const nativeEvent: any = e.nativeEvent;
        if (burstInputInitiated) {
          if(!Boolean((nativeEvent.timeStamp - burstInputInitiated) > 300)){
            burstStringHolder = burstStringHolder + nativeEvent.data;
          }
          else{
            burstInputInitiated = false;
            burstStringHolder = "";
            clearTimeout(timeoutHolder);
          }
        } else {
          let value;
          if (disabledManualInput && nativeEvent && nativeEvent.data) {
            burstStringHolder = burstStringHolder + nativeEvent.data;
            burstInputInitiated = nativeEvent.timeStamp;
            timeoutHolder = setTimeout(()=>{
              // parse and clear the burst string values here
              let isValidBurstInput = burstStringHolder.includes(".");
              if(isValidBurstInput){
                let parsedValue = extractNumericPart(burstStringHolder);
                onChange(parsedValue);
              }
              burstInputInitiated = false;
              burstStringHolder = "";
            },500);
          }
          if (!disabledManualInput) {
            value = e.target.value;
          }
          else{
            if(!burstInputInitiated){
              value = formData[fieldName] ?? ""
            }
          }
          onChange(value);
        }
      }}
      onKeyDown={(e) => {
        const key = e.key
        const isLetter = /^[#.0-9a-zA-Z \s,@/-]+$/.test(key)
        const isSpace = key === " "
        const isControlKey = key === "Backspace" || key === "Delete" || key === "ArrowLeft" || key === "ArrowRight"
        if (!isLetter && !isSpace && !isControlKey) {
          e.preventDefault()
        }

        if(e && (e.key === "Enter" || e.key === "Tab" || e.keyCode === 10) && disabledManualInput){
          e.preventDefault();
        }
      }}
      fullWidth
      label={label}
      value={formData[fieldName] ?? ''}
      error={Boolean(error)}
      helperText={Boolean(error) && error}
      size="small"
      disabled={disabled}
      required={Boolean(validation?.required)}
      defaultValue={defaultValue}
      InputLabelProps={{
        shrink:
          isFocused ||
          !isEmpty((formData as any)[fieldName]) ||
          (fieldName === 'customerTaxInvoicePreTaxValue' &&
            !isEmpty((formData as any).customerTaxInvoiceDeclaredValue)) ||
          (fieldName === 'customerTaxInvoiceDeclaredValue' &&
            !isEmpty((formData as any).customerTaxInvoicePreTaxValue))
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        if (decimalPlaces) {
          let decimalValue: number | string = isEmpty(
            formData[fieldName] as string
          )
            ? ""
            : Number(formData[fieldName]).toFixed(decimalPlaces);
          if (fieldType === "number") decimalValue = Number(decimalValue);

          onChange(decimalValue);
        }
      }}
    />
  );
}

// function DynamicCustomField<T>(props: TDynamicFieldProps<T>) {
//   const { RenderField } = props.config;
//   return RenderField ? <RenderField {...props} /> : <></>
// }

function DynamicCustomField<T>(props: TDynamicFieldProps<T>) {
  const { RenderField, component } = props.config;

  if (RenderField) {
    const Field = RenderField;
    return <Field {...props} />;
  }

  if (component) {
    const Component = component;
    return <Component {...props} />;
  }

  return <></>;
}

function DynamicConfirmationField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { fieldType, fieldName, label, onConfirm, isVerified, disabled,validation,defaultValue } = config;
  const getValue = () => formData[fieldName]
  return <TextField
    size='small'
    type={fieldType}
    onChange={(e) => onChange(e.target.value)}
    fullWidth label={label}
    value={getValue()}
    error={Boolean(error)}
    helperText={Boolean(error) && error}
    disabled={disabled}
    InputProps={{
      endAdornment: <IconButton onClick={() => !isVerified && onConfirm!(getValue(), formData)}>
        {isVerified ? <VerifiedUser color='success' /> : <Replay />}
      </IconButton>
    }}
    required ={Boolean(validation?.required)}
    onKeyDown={handleKeyDown}
    InputLabelProps={{ shrink: true }}
  />
}

function DynamicSelectField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { options, label, disabled, validation } = config;
  const fieldName = config.fieldName
  const stringFieldName = fieldName as string

  return <FormControl required ={Boolean(validation?.required)} fullWidth size='small' disabled={disabled}>
    <InputLabel id={`${stringFieldName}_label`}>{label}</InputLabel>
    <Select
      onChange={(e) => onChange(e.target.value)}
      labelId={`${stringFieldName}_label`}
      id={stringFieldName}
      label={label}
      value={formData[fieldName] || null}
      error={Boolean(error)}
      disabled={disabled}
      onKeyDown={handleKeyDown}
    >
      {options?.map((option, index) => {
        return <MenuItem selected={option.value === formData[fieldName]} key={index} value={option.value}>{option.label}</MenuItem>
      })}
    </Select>
    {Boolean(error) ? <FormHelperText error>{error}</FormHelperText> : <></>}
  </FormControl>
}

function DynamicAutocomplete<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { options, fieldName, label, disabled,validation, onChange: onChangeText,freeSolo } = config;
  const currentValue = (formData as any).city;
  return <Autocomplete
  size="small"
  fullWidth
  options={options || []}
  freeSolo={freeSolo}
  value={freeSolo ? currentValue : options?.find(option => option.value === formData[fieldName]) || null}
  getOptionLabel={(option) => {
    if (typeof option === 'string') {
      return option;
    }
    return option.label;
  }}
  onChange={(event, newValue) => {
    if (typeof newValue === 'string') {
      onChange(newValue);
    } else {
      onChange(newValue?.value || null);
    }
  }}
  disabled={disabled}
  renderInput={(params) => (
    <TextField
      label={label}
      error={Boolean(error)}
      helperText={error}
      onChange={(e) => {
        onChangeText && onChangeText(e.target.value, formData);
      }}
      {...params}
      required={Boolean(validation?.required)}
    />
  )}
  onKeyDown={handleKeyDown}
/>
}

function DynamicDateField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { fieldName, label, disableFuture, disabled, validation } = config;
  return <LocalizationProvider dateAdapter={AdapterMoment}>
    <MobileDatePicker
      slotProps={{
        textField: { fullWidth: true, error: Boolean(error), helperText: error, size: 'small', required : Boolean(validation?.required) },
        field: {
          InputProps: {
            endAdornment: <IconButton><CalendarIcon /></IconButton>
          }
        }
      }}
      label={label}
      onChange={(value) => onChange(value)}
      value={formData[fieldName] ? moment(formData[fieldName] as MomentInput) : null}
      disableFuture={disableFuture}
      disabled={disabled}
    />
  </LocalizationProvider>
}

function DynamicCheckbox<T>({ config, formData, onChange }: TDynamicFieldProps<T>) {
  const { fieldName, label, disabled } = config;
  return <FormControlLabel
    sx={{ color: "#7f7d76" }}
    control={
      <Checkbox
        size='small'
        onChange={(e, checked) => onChange(checked)}
        checked={Boolean(formData[fieldName])}
        disabled={disabled}
      />
    }
    label={label}
  />
}

function DynamicSwitch<T>({ config, formData, onChange }: TDynamicFieldProps<T>) {
  const { fieldName, label, disabled } = config;
  return <FormControlLabel
    control={<Switch
      size='small'
      onChange={(e, checked) => onChange(checked)}
      checked={Boolean(formData[fieldName])}
      disabled={disabled}
    />}
    label={label}
  />
}


//  These are the helper functions used in the form.
function getInitialValue<T>(config: TField<T>[]): T {
  return config.reduce((initialValues, field) => {
    return field.value != undefined ?
      { ...initialValues, [field.fieldName]: field.value } :
      field.initialValue != undefined ?
        { ...initialValues, [field.fieldName]: field.initialValue } :
        // field.fieldType === 'date' ?
        //   { ...initialValues, [field.fieldName]: moment() } :
          initialValues
  }, {} as T)
}

function validateForm<T>(config: TField<T>[], data: any) {
  const errors = []
  for (let fieldConfig of config) {
    const { fieldName, error } = validateField(fieldConfig, data[fieldConfig.fieldName], data)

    // Type of error property would be 'boolean' only if there is no error, so we push only in case of error.
    if (typeof error !== 'boolean') errors.push({ fieldName, error })
  }
  return errors
}

function validateField<T>(field: TField<T>, value: any, formData: T) {
  const { validation, fieldType, label } = field

  const fieldName: string = field.fieldName as string

  if (typeof validation !== 'object') return fieldError(fieldName)

  const { required, minLength, maxLength, min, max, contain, regex, email } = validation

  const requiredError = required instanceof Condition ?
    checkRequired(required.isConditionValid(formData), value, fieldName, label) :
    typeof required === 'boolean' ?
      checkRequired(required, value, fieldName, label) :
      false

  if (requiredError !== false) return requiredError

  const emailError = email !== undefined ? checkEmailError(email, value, fieldName, label) : false
  if (emailError !== false) return emailError

  const containError = contain === undefined ? false :
    Boolean((contain as string | string[] | number[])?.length) ?
      checkContainError(contain as string | string[] | number[], value, fieldName, label) :
      contain !== undefined ?
        checkContainError((contain as ConditionalValue<T>).value, value, fieldName, label) :
        false

  if (containError !== false) return containError

  const regexError = regex === undefined ? false :
    regex instanceof RegExp ?
      checkRegexError(regex, value, fieldName, label) :
      regex.isAnyConditionValid(formData) ?
        checkRegexError(regex.value, value, fieldName, label) :
        false

  if (regexError !== false) return regexError

  switch (fieldType) {
    case 'text':
    case 'password':
    case 'select':
    case 'autocomplete':
      const minLengthError = minLength === undefined ? false :
        typeof minLength === 'number' ?
          checkMinLengthError(minLength, value, fieldName, label) :
          minLength.isAnyConditionValid(formData) ?
            checkMinLengthError(minLength.value, value, fieldName, label) :
            false
      if (minLengthError !== false) return minLengthError

      const maxLengthError = maxLength === undefined ? false :
        typeof maxLength === 'number' ?
          checkMaxLengthError(maxLength, value, fieldName, label) :
          maxLength.isAnyConditionValid(formData) ?
            checkMaxLengthError(maxLength.value, value, fieldName, label) :
            false

      if (maxLengthError !== false) return maxLengthError

      return fieldError(fieldName)

    case 'number':
      const minError = min === undefined ? false :
        typeof min === 'number' ?
          checkMinError(min, value, fieldName, label) :
          min.isAnyConditionValid(formData) ?
            checkMinError(min.value, value, fieldName, label) :
            false

      if (minError !== false) return minError

      const maxError = max === undefined ? false :
        typeof max === 'number' ?
          checkMaxError(max, value, fieldName, label) :
          max.isAnyConditionValid(formData) ?
            checkMaxError(max.value, value, fieldName, label) :
            false

      if (maxError !== false) return maxError

      return fieldError(fieldName)

    default:
      return fieldError(fieldName)
  }

}

function checkRequired(required: boolean, value: any, field: string, label: string) {
  if (required && (typeof value === 'undefined' || value === null || String(value).length < 1)) {
    return fieldError(field, `${label} cannot be empty.`)
  }
  return false
}

function checkConditionalRequired<T>(condition: TCondition<T>, value: any, field: string, label: string, formData: T) {
  const fieldName: keyof T = condition.fieldName
  let error: false | TFormError = false
  switch (condition.operator) {
    case 'eq':
      if (formData[fieldName] === condition.value) error = checkRequired(true, value, field, label) as false | TFormError
      break;

    case 'ne':
      if (formData[fieldName] != condition.value) error = checkRequired(true, value, field, label) as false | TFormError
      break;
  }
  return error
}

function checkEmailError(email: boolean, value: any, field: string, label: string) {
  if (email && !emailRegex.test(value)) {
    return fieldError(field, `${label} is invalid.`)
  }
  return false
}

function checkMinLengthError(minLength: number, value: any, field: string, label: string) {
  if (typeof value === 'string' && value.length < minLength && value.length != 0) {
    return fieldError(field, `${label} must contain minimum ${minLength} characters.`)
  }
  return false
}

function checkMaxLengthError(maxLength: number, value: any, field: string, label: string) {
  if (typeof value === 'string' && value.length > maxLength) {
    return fieldError(field, `${label} cannot contain more than ${maxLength} characters.`)
  }
  return false
}

function checkMinError(min: number, value: any, field: string, label: string) {
  if (typeof value === 'number' && value < min) {
    return fieldError(field, `Value of ${label} cannot be less than ${min}.`)
  }
  return false
}

function checkMaxError(max: number, value: any, field: string, label: string) {
  if (typeof value === 'number' && value > max) {
    return fieldError(field, `Value of ${label} cannot be more than ${max}.`)
  }
  return false
}

function checkContainError(allowed: string[] | number[] | string, value: any, field: string, label: string) {
  if ((typeof value === 'number' || typeof value === 'string') && !allowed.includes(value as never)) {
    return fieldError(field, `Value of ${label} is invalid.`)
  }
  return false
}

function checkRegexError(regex: RegExp, value: any, field: string, label: string) {
  if(!value) return false
  if (!regex.test(value)) {
    return fieldError(field, `Value of ${label} is invalid.`)
  }
  return false
}

function fieldError(fieldName: string, error?: string) {
  return { fieldName, error: error || false }
}

export function getFormConfigDeepCopy<T>(fields: TField<T>[]) {
  const config: TField<T>[] = fields.map(field => {
    if(field.regexFilter !== undefined) {
      field.regexFilter = getRegexObject(field.regexFilter) as any
    }
    if(field.validation !== undefined && field.validation.regex instanceof RegExp) {
      field.validation.regex = getRegexObject(field.validation.regex) as any
    }
     return field
  })

  // for (let field of fields) {
  //   const { regexFilter, validation, ...restField } = field

  //   const newField: TField<T> = restField

  //   if (regexFilter !== undefined) {
  //     newField.regexFilter = getRegexObject(regexFilter) as any
  //   }

  //   if (validation !== undefined) {
  //     const { regex, ...restValidation } = validation
  //     newField.validation = restValidation
  //     if (regex !== undefined && regex instanceof RegExp) {
  //       newField.validation.regex = getRegexObject(regex) as any
  //     }
  //   }
  //   config.push(newField)
  // }

  const configCopy = getDeepCopy(config)

  const updatedConfig: TField<T>[] = configCopy.map(field => {
    if(field.regexFilter !== undefined) {
      field.regexFilter = getRegexFromObject(field.regexFilter as any) as any
    }
    if(field.validation !== undefined && field.validation.regex != undefined && (field.validation.regex as any).pattern != undefined) {
      field.validation.regex = getRegexFromObject(field.validation.regex as any) as any
    }
     return field
  })

  return updatedConfig
}

type TRegexObject = {
  pattern: any, 
  flags: any
}
function getRegexObject(regex: RegExp) {
  return {
    pattern: regex.source,
    flags: regex.flags
  }
}

function getRegexFromObject({pattern, flags}:TRegexObject) {
  return new RegExp(pattern, flags);
}

export default DynamicForm